import React, { useEffect, useState } from 'react';

import { Helmet } from 'react-helmet-async';
import {
  Navigate,
  type To,
  useLocation,
  type Location,
} from 'react-router-dom';

import BackgroundImage from '@/assets/login/background-image.jpg';
import ForParentsLogo from '@/assets/logo/for-parents.svg?react';
import Footer from '@/components/layouts/Footer';
import InformationHeader from '@/components/layouts/InformationHeader';
import { pageTitles } from '@/constants/pageTitles';
import LoginForm from '@/features/login/components/LoginForm';
import useAuthentication, {
  type AccessTokenResponse,
} from '@/hooks/common/useAuthentication';
import useFetch from '@/hooks/common/useFetch';
import useHomePath from '@/hooks/common/useHomePath';

import CSSModule from './LoginPage.module.scss';

export interface LocationState {
  redirectTo?: To;
}

interface FormValues {
  email_or_code: string;
  password: string;
  remember_me: boolean;
}

const LoginPage: React.FC = () => {
  const location = useLocation() as Location<LocationState>;
  const { login, isLoggedIn } = useAuthentication();
  const homePath = useHomePath();
  const [remember, setRemember] = useState(false);

  const { fetch, isFetching, error, data, status } =
    useFetch<AccessTokenResponse>('POST', '/api/school/auth/token');

  const onSubmit = (values: FormValues) => {
    setRemember(values.remember_me);
    try {
      void fetch({ body: values as unknown as Record<string, unknown> });
    } catch (e) {
      console.error({ message: 'Login Submit', error: e });
    }
  };

  useEffect(() => {
    if (data) {
      login(data.access_token);
    }
  }, [login, data, remember]);

  if (isLoggedIn) {
    const redirectTo = location.state?.redirectTo || homePath;
    return <Navigate replace to={redirectTo} />;
  }

  return (
    <div
      className={CSSModule.LoginPage}
      style={{ backgroundImage: `url(${BackgroundImage})` }}
    >
      <Helmet>
        <title>{pageTitles.login}</title>
      </Helmet>
      <InformationHeader />
      <div className={CSSModule.LoginPage__Inner}>
        <div className={CSSModule.LoginPage__Body}>
          <div className={CSSModule.LoginPage__BodyInner}>
            <div className={CSSModule.LoginPage__LogoWrapper}>
              <ForParentsLogo className={CSSModule.LoginPage__Logo} />
            </div>
            <div className={CSSModule.LoginPage__Form}>
              <LoginForm
                isFetching={isFetching}
                serverError={error?.message || null}
                onSubmit={onSubmit}
                serverResponseStatus={status}
              />
            </div>
          </div>
        </div>
        <Footer className={CSSModule.LoginPage__Footer} />
      </div>
    </div>
  );
};

export default LoginPage;
